import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from 'react';

import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'small' | 'default' | 'large';

type ButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  children: ReactNode;
  className?: string;
  destructive?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>;

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLElement, ButtonProps<ElementType>>(
  function Button(
    {
      as,
      children,
      className,
      destructive = false,
      fullWidth = false,
      iconOnly = false,
      size = 'default',
      variant = 'primary',
      ...rest
    },
    ref,
  ) {
    const Component = as ?? 'button';
    const componentProps =
      Component === 'button' && !('type' in rest)
        ? { ...rest, type: 'button' }
        : rest;

    return (
      <Component
        ref={ref as never}
        className={classNames(styles.button, className)}
        data-destructive={destructive ? 'true' : 'false'}
        data-full-width={fullWidth ? 'true' : 'false'}
        data-icon-only={iconOnly ? 'true' : 'false'}
        data-size={size}
        data-variant={variant}
        {...componentProps}
      >
        {children}
      </Component>
    );
  },
);

Button.displayName = 'Button';
